"use client";

import { Message } from "@/lib/core/crm/types";
import { useConfirmationDialog } from "@/lib/hooks/forms/use-confirmation-dialog";
import { useDirectMessageActions } from "@/lib/hooks/messages/useDirectMessageActions";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useReplyContext } from "../../contexts/ReplyContext";

export interface MessageActionHandlers {
  // Action handlers
  handleReply: () => void;
  handleForward: () => void;
  handleCreateTask: () => void;
  handleLogActivity: () => void;
  handleSendTemplate: () => void;
  handleArchive: () => void;
  handleDelete: () => void;
  handleMarkRead: () => void;
  handleMarkUnread: () => void;
  handleMarkImportant: () => void;
  handleMarkSpam: () => void;
  handlePrint: () => void;

  // State
  isActionLoading: boolean;
  actionError: string | null;

  // Confirmation dialogs
  ArchiveDialog: React.ComponentType;
  DeleteDialog: React.ComponentType;
  SpamDialog: React.ComponentType;
}

export function useMessageActionHandlers(message: Message): MessageActionHandlers {
  const {
    deleteMessage: deleteMessageAction,
    archiveMessage: archiveMessageAction,
    markAsRead: markAsReadAction,
    markAsUnread: markAsUnreadAction,
    markImportant: markImportantAction,
    markSpam: markSpamAction,
    isPending,
  } = useDirectMessageActions();

  const queryClient = useQueryClient();
  const { openModal } = useGlobalModalStack();
  const { setReplyingToMessage } = useReplyContext();

  // The inbox message list is cached under ["messages", ...]. The direct
  // action helpers bypass TanStack state, so we must invalidate this key after
  // each action for the list row to disappear / update.
  const refreshMessageList = () => {
    void queryClient.invalidateQueries({ queryKey: ["messages"] });
  };

  const { confirm: confirmArchive, ConfirmationDialog: ArchiveDialog } = useConfirmationDialog({
    title: "Archive Message?",
    description: "This message will be moved to the archive.",
    confirmText: "Archive",
  });

  const { confirm: confirmDelete, ConfirmationDialog: DeleteDialog } = useConfirmationDialog({
    title: "Delete Message?",
    description: "This message will be permanently deleted. This action cannot be undone.",
    confirmText: "Delete",
  });

  const { confirm: confirmSpam, ConfirmationDialog: SpamDialog } = useConfirmationDialog({
    title: "Mark as Spam?",
    description: "This message will be moved to spam and similar messages will be filtered.",
    confirmText: "Mark as Spam",
  });

  const handleReply = () => {
    // Set the context first to ensure it's available when the modal opens
    setReplyingToMessage(message);
    // Explicitly set mode to "reply" and pass the message to ensure proper initialization
    // The modal data will be used as a fallback if context hasn't propagated yet
    openModal(MODAL_IDS.messages.compose, {
      message,
      mode: "reply", // Explicitly set reply mode
    });
  };

  const handleForward = () => {
    // Open compose modal in forward mode
    // Don't set replyingToMessage for forwards - that's only for replies
    openModal(MODAL_IDS.messages.compose, {
      message,
      mode: "forward",
    });
  };

  const handleCreateTask = () => {
    openModal(MODAL_IDS.tasks.create, { message });
  };

  const handleLogActivity = async () => {
    // Open a modal to log activity from the message
    // This will create a communication record linked to the contact
    // We need to fetch the contact by email first
    try {
      const senderEmail = message.sender.email;
      // The modal will fetch the contact by email internally
      // Pass the email so the modal can look it up
      openModal(MODAL_IDS.messages.logActivity, { 
        message,
        senderEmail,
        contactEmail: senderEmail,
      });
    } catch (error) {
      console.error("Error opening log activity modal:", error);
    }
  };

  const handleSendTemplate = () => {
    // Open template selector modal
    openModal(MODAL_IDS.messages.sendTemplate, { message });
  };

  const handleArchive = () => {
    confirmArchive(async () => {
      try {
        await archiveMessageAction(message);
        refreshMessageList();
      } catch (error) {
        console.error("❌ handleArchive: Error archiving message:", error);
      }
    });
  };

  const handleDelete = () => {
    confirmDelete(async () => {
      try {
        await deleteMessageAction(message);
        refreshMessageList();
      } catch (error) {
        console.error("❌ handleDelete: Error deleting message:", error);
      }
    });
  };

  const handleMarkRead = async () => {
    try {
      await markAsReadAction(message);
      refreshMessageList();
    } catch (error) {
      console.error("❌ handleMarkRead: Error marking message as read:", error);
    }
  };

  const handleMarkUnread = async () => {
    try {
      await markAsUnreadAction(message);
      refreshMessageList();
    } catch (error) {
      console.error("❌ handleMarkUnread: Error marking message as unread:", error);
    }
  };

  const handleMarkImportant = async () => {
    try {
      await markImportantAction(message);
      refreshMessageList();
    } catch (error) {
      console.error("❌ handleMarkImportant: Error marking message as important:", error);
    }
  };

  const handleMarkSpam = () => {
    confirmSpam(async () => {
      try {
        await markSpamAction(message);
        refreshMessageList();
      } catch (error) {
        console.error("❌ handleMarkSpam: Error marking message as spam:", error);
      }
    });
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
      toast.error("Enable popups to print");
      return;
    }

    const escapeHtml = (value: string): string =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const subject = escapeHtml(message.subject || "(No Subject)");
    const senderName = escapeHtml(message.sender?.name || "");
    const senderEmail = escapeHtml(message.sender?.email || "");
    const senderLine = senderEmail
      ? `${senderName} &lt;${senderEmail}&gt;`
      : senderName || "Unknown sender";
    const recipients = escapeHtml(
      (message.recipients || [])
        .map((recipient) => recipient.email || recipient.name)
        .filter(Boolean)
        .join(", ")
    );
    const dateValue = message.receivedDateTime
      ? escapeHtml(new Date(message.receivedDateTime).toLocaleString())
      : "";

    // message.body may be HTML — render it as-is inside the print document.
    const bodyHtml = message.body || "";

    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${subject}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
      .meta { border-bottom: 1px solid #ccc; padding-bottom: 12px; margin-bottom: 16px; }
      .meta h1 { font-size: 18px; margin: 0 0 8px; }
      .meta p { margin: 2px 0; font-size: 13px; color: #333; }
      .body { font-size: 14px; line-height: 1.5; }
    </style>
  </head>
  <body>
    <div class="meta">
      <h1>${subject}</h1>
      <p><strong>From:</strong> ${senderLine}</p>
      ${recipients ? `<p><strong>To:</strong> ${recipients}</p>` : ""}
      ${dateValue ? `<p><strong>Date:</strong> ${dateValue}</p>` : ""}
    </div>
    <div class="body">${bodyHtml}</div>
  </body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return {
    // Action handlers
    handleReply,
    handleForward,
    handleCreateTask,
    handleLogActivity,
    handleSendTemplate,
    handleArchive,
    handleDelete,
    handleMarkRead,
    handleMarkUnread,
    handleMarkImportant,
    handleMarkSpam,
    handlePrint,

    // State
    isActionLoading: isPending,
    actionError: null,

    // Confirmation dialogs
    ArchiveDialog,
    DeleteDialog,
    SpamDialog,
  };
}
